import { createFileRoute } from "@tanstack/react-router";
import { requireAuth } from "~/lib/auth-guard";
import { ReferralWidget } from "~/components/ReferralWidget";

export const Route = createFileRoute("/referral")({
  beforeLoad: async () => { await requireAuth(); },
  head: () => ({
    meta: [
      { name: "robots", content: "noindex, nofollow" },
      { title: "Empfehlungsprogramm — Alchema" },
    ],
  }),
  component: ReferralPage,
});

/* ── SVG Icons (kept, used by ReferralPage) ── */

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function StarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

function ReferralPage() {
  const tiers = [
    {
      icon: <UsersIcon className="h-5 w-5" />,
      count: "3",
      reward: "1 Gratis-Monat",
      description: "Lade 3 Freunde ein und erhalte 1 Monat Alchema Pro kostenlos.",
      color: "var(--accent)",
    },
    {
      icon: <StarIcon className="h-5 w-5" />,
      count: "10",
      reward: "3 Gratis-Monate",
      description: "Mit 10 erfolgreichen Einladungen schaltest du 3 Monate Pro frei.",
      color: "var(--success)",
    },
  ];

  return (
    <div className="mx-auto max-w-2xl p-6 sm:p-8">
      {/* Attio page header */}
      <div className="animate-fade-up">
        <h1
          className="font-display text-[22px] font-semibold tracking-tight"
          style={{ color: "var(--text)", fontFamily: "var(--font-display, inherit)" }}
        >
          Freunde einladen, Gratis-Monate verdienen
        </h1>
        <p
          className="mt-1 text-sm"
          style={{ color: "var(--text-muted)" }}
        >
          Teile Alchema mit deinen Freunden. Fuer jede erfolgreiche Anmeldung bekommt ihr beide Vorteile.
        </p>
      </div>

      {/* Widget */}
      <div className="mt-8">
        <ReferralWidget />
      </div>

      {/* Reward tiers — stat block pattern */}
      <div className="mt-10">
        <p className="section-label mb-4">Belohnungsstufen</p>
        <div
          className="rounded-[10px] border overflow-hidden"
          style={{ borderColor: "var(--border-subtle)" }}
        >
          {tiers.map((tier, i) => (
            <div
              key={tier.count}
              className="flex items-center justify-between px-4 py-4"
              style={{
                borderBottom:
                  i < tiers.length - 1 ? "1px solid var(--border-subtle)" : "none",
              }}
            >
              <div className="flex items-center gap-3">
                <span style={{ color: tier.color }}>{tier.icon}</span>
                <div>
                  <p
                    className="text-sm font-medium"
                    style={{ color: "var(--text)" }}
                  >
                    {tier.description}
                  </p>
                </div>
              </div>
              {/* Stat block */}
              <div className="ml-6 text-right shrink-0">
                <p
                  className="text-2xl font-bold tabular-nums leading-none"
                  style={{ color: tier.color }}
                >
                  {tier.count}
                </p>
                <p
                  className="mt-0.5 text-[11px] uppercase tracking-wider"
                  style={{ color: "var(--text-muted)" }}
                >
                  Einladungen
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* How it works */}
      <div className="mt-10 mb-8">
        <p className="section-label mb-4">So funktioniert es</p>
        <div
          className="rounded-[10px] border overflow-hidden"
          style={{ borderColor: "var(--border-subtle)" }}
        >
          {[
            {
              step: "1",
              title: "Teile deinen Link",
              desc: "Sende deinen persoenlichen Einladungslink per WhatsApp, E-Mail oder kopiere ihn.",
            },
            {
              step: "2",
              title: "Freund registriert sich",
              desc: "Dein Freund erstellt ein kostenloses Alchema-Konto ueber deinen Link.",
            },
            {
              step: "3",
              title: "Beide profitieren",
              desc: "Du sammelst Einladungen und schaltest Gratis-Monate frei.",
            },
          ].map((item, i) => (
            <div
              key={item.step}
              className="flex items-start gap-4 px-4 py-4"
              style={{
                borderBottom: i < 2 ? "1px solid var(--border-subtle)" : "none",
              }}
            >
              {/* Step number as stat block */}
              <div className="shrink-0 text-right">
                <p
                  className="text-xl font-bold tabular-nums leading-none"
                  style={{ color: "var(--text-muted)" }}
                >
                  {item.step}
                </p>
              </div>
              <div>
                <p
                  className="text-sm font-medium"
                  style={{ color: "var(--text)" }}
                >
                  {item.title}
                </p>
                <p
                  className="mt-0.5 text-[13px] leading-relaxed"
                  style={{ color: "var(--text-muted)" }}
                >
                  {item.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
